// Beta programme landing page — public, recruits testers before launch.
// Mirrors the visual system of About.jsx and Landing.jsx (teal accents,
// soft surfaces, generous spacing, mobile-first).

import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  MessagesSquare,
  ClipboardCheck,
  Sparkles,
  CalendarCheck,
  UserRound,
} from "lucide-react";

const APPLY_URL = "https://megnificentcreative.formaloo.me/5r8ht2";

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)]";

const IconBadge = ({ icon: Icon }) => (
  <span className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center flex-shrink-0">
    <Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
  </span>
);

const ApplyCTA = ({ label, testId, size = "lg" }) => (
  <a
    href={APPLY_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-colors bg-[#116B6E] hover:bg-[#0F5658] text-white shadow-[0_1px_2px_rgba(15,21,23,0.06)] focus:outline-none focus:ring-2 focus:ring-[#1F9FA4]/50 ${
      size === "lg"
        ? "px-6 py-3 text-base sm:text-lg"
        : "px-5 py-2.5 text-sm"
    }`}
    data-testid={testId}
  >
    {label}
    <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
  </a>
);

const BulletList = ({ items, testId }) => (
  <ul className="mt-5 space-y-2.5" data-testid={testId}>
    {items.map((line, i) => (
      <li key={i} className="flex gap-3 text-[15px] leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
        <span
          aria-hidden="true"
          className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1F9FA4] flex-shrink-0"
        />
        <span>{line}</span>
      </li>
    ))}
  </ul>
);

const StatChip = ({ label, value, testId }) => (
  <div
    className={`${cardSurface} px-5 py-4 flex flex-col gap-1`}
    data-testid={testId}
  >
    <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#1F9FA4]">
      {label}
    </div>
    <div className="text-[#1A2225] dark:text-[#F4F8F9] font-heading text-lg font-semibold">
      {value}
    </div>
  </div>
);

// Static "mobile mockup" for the hero — a compact device frame showing
// a stylised dashboard preview. Deliberately simple (no live cycling
// screens like Landing) so the Beta page stays quick to render and
// focused on the recruitment message.
const BetaPhoneMockup = () => (
  <div className="relative" data-testid="beta-hero-phone-mockup">
    <div className="relative w-[240px] sm:w-[280px] h-[500px] sm:h-[580px] rounded-[2.4rem] bg-[#1A2225] dark:bg-[#080B0C] p-3 shadow-[0_30px_60px_-20px_rgba(15,21,23,0.35)] ring-1 ring-black/5">
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-6 bg-[#1A2225] dark:bg-[#080B0C] rounded-b-2xl z-10" />
      <div className="w-full h-full rounded-[1.9rem] overflow-hidden bg-white dark:bg-[#0E1416] relative">
        {/* Status bar */}
        <div className="h-8 flex items-end justify-between px-5 pb-1">
          <span className="text-[10px] font-semibold text-[#1A2225] dark:text-[#F4F8F9]">9:41</span>
          <span className="text-[9px] text-[#48595E]">•••</span>
        </div>
        {/* Header */}
        <div className="px-5 pt-3 pb-4">
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#1F9FA4]">
            Overview
          </div>
          <div className="font-heading text-lg font-semibold text-[#1A2225] dark:text-[#F4F8F9] mt-1">
            Hello, Alex
          </div>
        </div>
        {/* Today card */}
        <div className="mx-4 rounded-lg bg-[#E0F2F2] dark:bg-[#0F3234] p-3">
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#116B6E] dark:text-[#7ADFE0]">
            Today
          </div>
          <div className="text-[11px] text-[#1A2225] dark:text-[#F4F8F9] mt-1">
            You have <span className="font-semibold">Mia</span> &amp;{" "}
            <span className="font-semibold">Noah</span>
          </div>
        </div>
        {/* Upcoming items */}
        <div className="mx-4 mt-3 space-y-2">
          {[
            { name: "Mia", detail: "Handover · Fri 20:00", dot: "#1F9FA4" },
            { name: "Noah", detail: "With you · all week", dot: "#B6820E" },
            { name: "School run", detail: "Mon 08:15", dot: "#7A5AA6" },
          ].map((row) => (
            <div
              key={row.name}
              className="flex items-center gap-2.5 rounded-md border border-[#E5EBEC] dark:border-[#2A3539] px-3 py-2"
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: row.dot }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-medium text-[#1A2225] dark:text-[#F4F8F9] truncate">
                  {row.name}
                </div>
                <div className="text-[9px] text-[#6F828A] truncate">
                  {row.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* KPI row */}
        <div className="mx-4 mt-4 grid grid-cols-2 gap-2">
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#2A3539] px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Unread</div>
            <div className="text-lg font-heading font-semibold text-[#1A2225] dark:text-[#F4F8F9] mt-0.5">0</div>
          </div>
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#2A3539] px-3 py-2.5">
            <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Outstanding</div>
            <div className="text-lg font-heading font-semibold text-[#B6820E] mt-0.5">€204</div>
          </div>
        </div>
      </div>
    </div>
    <div
      aria-hidden="true"
      className="absolute inset-0 -z-10 blur-3xl bg-[#1F9FA4]/20 dark:bg-[#1F9FA4]/15 rounded-full"
    />
  </div>
);

export default function Beta() {
  return (
    <div
      className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#E8EEF0]"
      data-testid="beta-page"
    >
      {/* Header */}
      <header
        className="sticky top-0 z-30 border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur"
        data-testid="beta-header"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="beta-brand-logo">
            <Logo size={32} />
            <span className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
              CoParent Console
            </span>
          </Link>
          <Link
            to="/"
            className="text-sm text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] inline-flex items-center gap-1.5"
            data-testid="beta-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Home</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="beta-hero">
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#E0F2F2]/60 via-white to-white dark:from-[#0F2F30]/70 dark:via-[#0E1416] dark:to-[#0E1416]"
        />
        <div
          aria-hidden="true"
          className="absolute -top-32 -right-32 w-[26rem] h-[26rem] rounded-full bg-[#1F9FA4]/10 dark:bg-[#1F9FA4]/15 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-14 sm:pb-20">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Copy column */}
            <div className="lg:col-span-7">
              <Eyebrow>CoParent Console Beta Programme</Eyebrow>
              <h1 className="font-heading text-[2.15rem] leading-[1.1] sm:text-5xl lg:text-[3.25rem] lg:leading-[1.05] font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
                Help us shape a better way to manage co-parenting.
              </h1>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7] max-w-2xl">
                CoParent Console is moving into its final phase of testing ahead
                of our official launch. We&apos;re inviting 20 parents to use the
                platform over 3 to 4 weeks and help us identify any final bugs,
                usability issues or features that could make the experience
                even better.
              </p>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#48595E] dark:text-[#B6C2C7] max-w-2xl">
                Everyone who completes the beta programme will receive{" "}
                <span className="text-[#116B6E] dark:text-[#3FB8BC] font-semibold">
                  6 months&apos; free access
                </span>{" "}
                to CoParent Console as a thank you for helping us shape the platform.
              </p>

              {/* CTA on its own line */}
              <div className="mt-10">
                <ApplyCTA label="Apply to Join the Beta" testId="beta-hero-cta" />
              </div>

              {/* Info row — three cards, evenly spaced, stack on mobile */}
              <div
                className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-2xl"
                data-testid="beta-hero-info-row"
              >
                <StatChip label="Places" value="20" testId="beta-hero-stat-places" />
                <StatChip label="Duration" value="3 to 4 weeks" testId="beta-hero-stat-duration" />
                <StatChip label="Reward" value="6 months free" testId="beta-hero-stat-reward" />
              </div>
            </div>

            {/* Mobile mockup column */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <BetaPhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* What is CoParent Console */}
      <section
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20"
        data-testid="beta-what-is"
      >
        <Eyebrow>What is CoParent Console?</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
          One place for the practical side of co-parenting.
        </h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
          <p>
            CoParent Console is a platform designed to help separated parents
            manage and keep track of important co-parenting information in one place.
          </p>
          <p>
            It brings together communication, schedules, shared expenses, documents,
            requests and records, helping you stay organised and maintain a clear
            record of what has happened.
          </p>
          <p>
            It has been designed with the realities of separation and high-conflict
            co-parenting in mind.
          </p>
        </div>
      </section>

      {/* Final phase of testing */}
      <section
        className="bg-[#F7FAFA] dark:bg-[#0F1618] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="beta-real-world"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <Eyebrow>The final phase of testing</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            The final phase of testing.
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            <p>
              CoParent Console has been developed around the practical challenges
              separated parents face, and we&apos;re now entering our final beta
              phase before official launch.
            </p>
            <p>
              We&apos;re looking for 20 parents with a range of co-parenting
              experiences to put the platform through its paces in real-world use
              and help us make the final refinements.
            </p>
            <p>
              We want to hear about anything that could make the experience better,
              from a button that isn&apos;t where you expected it to be, to a
              feature you&apos;d find useful, or a bug that needs fixing.
            </p>
            <p>
              You don&apos;t need to be particularly technical, and you don&apos;t
              need to be experiencing high conflict. We want a genuine mix of
              co-parenting experiences.
            </p>
          </div>
        </div>
      </section>

      {/* What will I be asked to do */}
      <section
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20"
        data-testid="beta-what-do"
      >
        <Eyebrow>What will I be asked to do?</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
          Our final beta testing phase will run for approximately 3 to 4 weeks.
        </h2>
        <p className="mt-5 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
          During that time, we&apos;ll ask you to:
        </p>
        <BulletList
          testId="beta-what-do-list"
          items={[
            "Set up and explore your CoParent Console account",
            "Use the features that are relevant to your situation",
            "Use the platform regularly during the beta period",
            "Tell us when something is confusing or difficult to use",
            "Report bugs or problems you encounter",
            "Complete a short weekly feedback check-in",
            "Share your overall experience with us at the end of the programme",
          ]}
        />
        <div
          className={`${cardSurface} p-6 mt-8 flex gap-4`}
          data-testid="beta-small-things-callout"
        >
          <IconBadge icon={MessagesSquare} />
          <div>
            <h3 className="font-heading text-lg font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
              We&apos;re interested in the small things too.
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
              If a button doesn&apos;t make sense, something takes too many steps,
              you can&apos;t find what you&apos;re looking for, or you find yourself thinking
              &ldquo;I wish it did this&rdquo;, we want to hear about it.
            </p>
          </div>
        </div>
      </section>

      {/* What you'll get */}
      <section
        className="bg-[#F7FAFA] dark:bg-[#0F1618] border-y border-[#E5EBEC] dark:border-[#1F2A2D]"
        data-testid="beta-what-get"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <Eyebrow>What you&apos;ll get</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
            Give us 3 to 4 weeks. Get 6 months free.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            As a thank you for helping us refine CoParent Console ahead of launch,
            everyone who completes the beta programme will receive:
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon: Sparkles,
                title: "6 months&apos; free access",
                desc: "Full access to CoParent Console after the beta ends.",
                testid: "beta-perk-free-months",
              },
              {
                icon: ClipboardCheck,
                title: "Full access throughout the beta",
                desc: "Every feature, unlocked for the whole 3&ndash;4 weeks.",
                testid: "beta-perk-full-access",
              },
              {
                icon: CalendarCheck,
                title: "Early access before official launch",
                desc: "You&apos;ll be using CoParent Console before the public release.",
                testid: "beta-perk-early-access",
              },
              {
                icon: Users,
                title: "Direct influence on the platform",
                desc: "The opportunity to shape improvements and future features.",
                testid: "beta-perk-influence",
              },
            ].map((p) => (
              <div
                key={p.testid}
                className={`${cardSurface} p-5 flex gap-4`}
                data-testid={p.testid}
              >
                <IconBadge icon={p.icon} />
                <div>
                  <h3
                    className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]"
                    dangerouslySetInnerHTML={{ __html: p.title }}
                  />
                  <p
                    className="mt-1.5 text-sm leading-relaxed text-[#48595E] dark:text-[#B6C2C7]"
                    dangerouslySetInnerHTML={{ __html: p.desc }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div
            className={`${cardSurface} p-5 mt-4 flex gap-4`}
            data-testid="beta-perk-new-features"
          >
            <IconBadge icon={Sparkles} />
            <div>
              <h3 className="font-heading text-base font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
                Early access to selected new features
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
                As they are developed, you&apos;ll be first to try them.
              </p>
            </div>
          </div>

          <p className="mt-8 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
            Your feedback will play a direct role in the final refinements
            before official launch.
          </p>
        </div>
      </section>

      {/* Who can apply */}
      <section
        className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20"
        data-testid="beta-who-apply"
      >
        <Eyebrow>Who can apply?</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
          A broad mix of experiences.
        </h2>
        <div className="mt-6 space-y-4 text-base leading-relaxed text-[#48595E] dark:text-[#B6C2C7]">
          <p>
            We&apos;d love to hear from you if you are a separated or divorced parent
            currently navigating co-parenting.
          </p>
          <p>
            We&apos;re particularly looking for a broad mix of experiences, including
            parents who have straightforward co-parenting arrangements as well as
            those dealing with communication difficulties, changing arrangements or
            high-conflict situations.
          </p>
          <p>
            You&apos;ll need to be willing to use CoParent Console regularly for
            approximately 3 to 4 weeks and give open, constructive feedback about
            your experience.
          </p>
          <p className="font-medium text-[#1A2225] dark:text-[#F4F8F9]">
            No technical knowledge is required.
          </p>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <IconBadge icon={UserRound} />
          <span className="text-sm text-[#48595E] dark:text-[#B6C2C7]">
            Separated · Divorced · Currently co-parenting
          </span>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative overflow-hidden bg-[#0E3E40] dark:bg-[#0B2E30]"
        data-testid="beta-final-cta"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none bg-gradient-to-b from-[#116B6E]/40 via-transparent to-transparent"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -left-32 w-[26rem] h-[26rem] rounded-full bg-[#1F9FA4]/20 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center">
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7ADFE0]">
            Interested in taking part?
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-3 text-white">
            20 places. 2 minutes to apply.
          </h2>
          <p className="mt-5 text-base sm:text-lg leading-relaxed text-[#B6E2E3] max-w-xl mx-auto">
            We&apos;re keeping this beta small, with just 20 places, so we can properly
            listen to the people taking part and act on the feedback we receive.
          </p>
          <p className="mt-3 text-sm text-[#8FC7C8]">
            The application takes approximately 2 minutes to complete.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <ApplyCTA label="Apply for the Beta Programme" testId="beta-final-cta-btn" />
            <p className="text-xs text-[#8FC7C8] max-w-md">
              Selected participants will be contacted with details of the beta
              start date and next steps.
            </p>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer
        className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416]"
        data-testid="beta-footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-xs text-[#6F828A]">
          &copy; {new Date().getFullYear()} CoParent Console.{" "}
          <Link
            to="/"
            className="hover:text-[#116B6E] dark:hover:text-[#3FB8BC] underline-offset-2 hover:underline"
            data-testid="beta-footer-home"
          >
            Home
          </Link>
        </div>
      </footer>
    </div>
  );
}
